import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../utils/api';
import { useTranslation } from 'react-i18next';
import { FaUser, FaMapMarkerAlt, FaPhone, FaLock } from 'react-icons/fa';
import '../styles/Profile.css';

const Profile = () => {
  const { user, loadUser } = useAuth();
  const { t } = useTranslation();
  
  const [editMode, setEditMode] = useState(false);
  const [passwordMode, setPasswordMode] = useState(false);
  
  const [formData, setFormData] = useState({
    address: user?.address || {},
    contactPerson: user?.contactPerson || {}
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await authAPI.updateProfile(formData);
      await loadUser();
      setEditMode(false);
      setMessage({ type: 'success', text: t('profileUpdated') });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || t('profileUpdateError', 'Error updating profile')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: t('passwordsNotMatch') });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: t('passwordTooShort') });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await authAPI.updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      setPasswordMode(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setMessage({ type: 'success', text: t('passwordChanged') });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || t('passwordChangeError', 'Error changing password')
      });
    } finally {
      setLoading(false);
    }
  };

  const getDayLabel = (day) => {
    const labels = {
      monday: t('monday', 'Monday'),
      tuesday: t('tuesday', 'Tuesday'),
      wednesday: t('wednesday', 'Wednesday'),
      thursday: t('thursday', 'Thursday'),
      friday: t('friday', 'Friday'),
      saturday: t('saturday', 'Saturday'),
      sunday: t('sunday', 'Sunday')
    };
    return labels[day] || day;
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        <h1><FaUser /> {t('myProfile')}</h1>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="profile-section">
          <h2>{t('basicInfo', 'Basic information')}</h2>
          <div className="info-grid">
            <div className="info-item">
              <strong>{t('email')}:</strong>
              <span>{user?.email}</span>
            </div>
            <div className="info-item">
              <strong>{t('restaurantName')}:</strong>
              <span>{user?.restaurantName}</span>
            </div>
            <div className="info-item">
              <strong>{t('userRole')}:</strong>
              <span>{user?.role === 'admin' ? t('admin') : t('client')}</span>
            </div>
            <div className="info-item">
              <strong>{t('status')}:</strong>
              <span className={user?.isActive ? 'status-active' : 'status-inactive'}>
                {user?.isActive ? t('active') : t('blocked', 'Blocked')}
              </span>
            </div>
          </div>
        </div>

        {!editMode ? (
          <>
            <div className="profile-section">
              <div className="section-header">
                <h2><FaMapMarkerAlt /> {t('address')}</h2>
                {user?.role !== 'admin' && (
                  <button onClick={() => setEditMode(true)} className="btn-edit">
                    {t('edit')}
                  </button>
                )}
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <strong>{t("address", "Aadress")}:</strong>
                  <span>{user?.address?.street || '-'}</span>
                </div>
                <div className="info-item">
                  <strong>{t('city', 'City')}:</strong>
                  <span>{user?.address?.city || '-'}</span>
                </div>
                <div className="info-item">
                  <strong>{t('postalCode', 'Postal code')}:</strong>
                  <span>{user?.address?.postalCode || '-'}</span>
                </div>
                <div className="info-item">
                  <strong>{t('country', 'Country')}:</strong>
                  <span>{user?.address?.country || '-'}</span>
                </div>
              </div>
            </div>

            <div className="profile-section">
              <h2><FaPhone /> {t('contactPerson')}</h2>
              <div className="info-grid">
                <div className="info-item">
                  <strong>{t('firstName', 'First name')}:</strong>
                  <span>{user?.contactPerson?.firstName || '-'}</span>
                </div>
                <div className="info-item">
                  <strong>{t('lastName', 'Last name')}:</strong>
                  <span>{user?.contactPerson?.lastName || '-'}</span>
                </div>
                <div className="info-item">
                  <strong>{t('phone')}:</strong>
                  <span>{user?.contactPerson?.phone || '-'}</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <form onSubmit={handleUpdateProfile} className="profile-form">
            <div className="profile-section">
              <h2><FaMapMarkerAlt /> {t('address')}</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>{t("address", "Aadress")}</label>
                  <input
                    type="text"
                    value={formData.address?.street || ''}
                    onChange={(e) => handleInputChange('address', 'street', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>{t('city', 'City')}</label>
                  <input
                    type="text"
                    value={formData.address?.city || ''}
                    onChange={(e) => handleInputChange('address', 'city', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>{t('postalCode', 'Postal code')}</label>
                  <input
                    type="text"
                    value={formData.address?.postalCode || ''}
                    onChange={(e) => handleInputChange('address', 'postalCode', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>{t('country', 'Country')}</label>
                  <input
                    type="text"
                    value={formData.address?.country || ''}
                    onChange={(e) => handleInputChange('address', 'country', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="profile-section">
              <h2><FaPhone /> {t('contactPerson')}</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>{t('firstName', 'First name')}</label>
                  <input
                    type="text"
                    value={formData.contactPerson?.firstName || ''}
                    onChange={(e) => handleInputChange('contactPerson', 'firstName', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>{t('lastName', 'Last name')}</label>
                  <input
                    type="text"
                    value={formData.contactPerson?.lastName || ''}
                    onChange={(e) => handleInputChange('contactPerson', 'lastName', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>{t('phone')}</label>
                  <input
                    type="tel"
                    value={formData.contactPerson?.phone || ''}
                    onChange={(e) => handleInputChange('contactPerson', 'phone', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? t('saving', 'Saving...') : t('save')}
              </button>
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => setEditMode(false)}
                disabled={loading}
              >
                {t('cancel')}
              </button>
            </div>
          </form>
        )}

        {user?.deliveryRules && user.role !== 'admin' && (
          <div className="profile-section">
            <h2>{t('deliveryRules', 'Delivery rules')}</h2>
            <div className="delivery-rules">
              <div className="info-item">
                <strong>{t('deliveryDays', 'Delivery days')}:</strong>
                <span>
                  {user.deliveryRules.days?.map(day => getDayLabel(day)).join(', ')}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="profile-section">
          <div className="section-header">
            <h2><FaLock /> {t('security', 'Security')}</h2>
            {!passwordMode && (
              <button onClick={() => setPasswordMode(true)} className="btn-edit">
                {t('changePassword')}
              </button>
            )}
          </div>

          {passwordMode && (
            <form onSubmit={handleUpdatePassword} className="password-form">
              <div className="form-group">
                <label>{t('currentPassword')}</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('newPassword')}</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  required
                  minLength="6"
                />
              </div>
              <div className="form-group">
                <label>{t('confirmPassword')}</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                  required
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? t('changing', 'Changing...') : t('changePassword')}
                </button>
                <button 
                  type="button" 
                  className="btn-secondary" 
                  onClick={() => {
                    setPasswordMode(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  disabled={loading}
                >
                  {t('cancel')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;

